import { useEffect, useRef } from 'react';

export const useIframeResize = (containerRef, options = {}) => {
  const {
    debounceMs = 100,
    basePadding = 30,
    extraPaddingForLastSection = 10,
    MAX_HEIGHT = 100000
  } = options;

  const timeoutRef = useRef(null);
  const lastHeightRef = useRef(0);
  const isSendingRef = useRef(false);
  const lastUpdateTimeRef = useRef(0);
  const forceUpdateRef = useRef(false);
  const updateCooldownMs = 300;

  const getMainContent = () => {
    return document.querySelector('.skipass-pricing-layout') ||
           document.querySelector('.skipass-main-content') ||
           document.querySelector('.skipass-App') || 
           document.querySelector('main') ||
           document.body;
  };

  const hasExpandedAccordions = () => {
    return document.querySelectorAll('.skipass-accordion-header.skipass-expanded').length > 0;
  };

  const calculateFromSections = () => {
    let height = 0;
    const accordionSections = document.querySelectorAll('.skipass-accordion-section');
    
    accordionSections.forEach(section => {
      const header = section.querySelector('.skipass-accordion-header');
      const isExpanded = header && header.classList.contains('skipass-expanded');
      
      if (isExpanded) {
        const sectionScrollHeight = section.scrollHeight || 0;
        const sectionOffsetHeight = section.offsetHeight || 0;
        height += Math.max(sectionScrollHeight, sectionOffsetHeight);
      } else {
        height += section.offsetHeight || 0;
      }
    });
    
    const cartSection = document.querySelector('.skipass-cart-section');
    if (cartSection) {
      height += cartSection.offsetHeight || 0;
    }
    
    const messengerContainer = document.querySelector('.skipass-messenger-instructions-container');
    if (messengerContainer) {
      const messengerSection = document.querySelector('.skipass-messenger-instructions-section');
      if (messengerSection && messengerSection.offsetHeight > 0) {
        height += messengerContainer.offsetHeight || messengerSection.offsetHeight || 0;
      }
    }
    
    const headerSection = document.querySelector('header');
    if (headerSection) {
      height += headerSection.offsetHeight || 0;
    }
    
    return height + basePadding;
  };

  const calculateExpandedHeight = (calculatedFromSections, mainContent) => {
    const bodyScrollHeight = document.body.scrollHeight || 0;
    const htmlScrollHeight = document.documentElement.scrollHeight || 0;
    
    let maxScrollHeight = Math.max(bodyScrollHeight, htmlScrollHeight);
    
    if (mainContent && mainContent.scrollHeight) {
      maxScrollHeight = Math.max(maxScrollHeight, mainContent.scrollHeight);
    }
    
    let boundingHeight = 0;
    if (mainContent) {
      const rect = mainContent.getBoundingClientRect();
      boundingHeight = rect.height || 0;
    }
    
    const containerHeight = mainContent ? Math.max(
      mainContent.scrollHeight || 0,
      mainContent.offsetHeight || 0
    ) : 0;
    
    let actualHeight = Math.max(
      maxScrollHeight,
      bodyScrollHeight,
      htmlScrollHeight,
      calculatedFromSections,
      containerHeight,
      boundingHeight
    );
    
    if (maxScrollHeight > 0) {
      actualHeight = Math.max(actualHeight, maxScrollHeight);
    }
    
    if (maxScrollHeight > calculatedFromSections * 1.05) {
      actualHeight = Math.max(actualHeight, maxScrollHeight);
    }
    
    if (maxScrollHeight > actualHeight * 0.9) {
      actualHeight = Math.max(actualHeight, maxScrollHeight);
    }
    
    if (maxScrollHeight > 0 && maxScrollHeight >= calculatedFromSections) {
      actualHeight = Math.max(actualHeight, maxScrollHeight);
    }
    
    return actualHeight;
  };

  const calculateCollapsedHeight = (calculatedFromSections) => {
    const pricingLayout = document.querySelector('.skipass-pricing-layout');
    const bodyOffsetHeight = document.body.offsetHeight || 0;
    const htmlOffsetHeight = document.documentElement.offsetHeight || 0;
    
    if (pricingLayout) {
      let collapsedHeight = pricingLayout.offsetHeight || 0;
      
      if (collapsedHeight < 100) {
        collapsedHeight = Math.max(calculatedFromSections, collapsedHeight);
      }
      
      if (collapsedHeight < 100) {
        collapsedHeight = Math.max(bodyOffsetHeight, htmlOffsetHeight, collapsedHeight);
      }
      
      return collapsedHeight;
    }
    
    return Math.max(
      calculatedFromSections,
      bodyOffsetHeight,
      htmlOffsetHeight
    );
  };

  const sendHeight = () => {
    const isInIframe = window.parent && window.parent !== window;
    
    if (!isInIframe || isSendingRef.current) {
      return;
    }
    
    const formPage = document.querySelector('.skipass-form-page');
    const isFormPageVisible = formPage && formPage.offsetHeight > 0 && 
                             window.getComputedStyle(formPage).display !== 'none';
    
    if (isFormPageVisible) {
      const productTable = document.querySelector('.skipass-product-table') || 
                          document.querySelector('.skipass-table-body') ||
                          document.querySelector('.skipass-order-items');
      let productRowsCount = 0;
      if (productTable) {
        const rows = productTable.querySelectorAll('.skipass-table-row, tr, .skipass-product-row');
        productRowsCount = rows.length;
      }
      
      const baseHeight = 861;
      const productHeight = Math.max(productRowsCount, 1) * 50;
      const minFormHeight = baseHeight + productHeight;
      
      const formPageHeight = formPage.offsetHeight || formPage.scrollHeight || minFormHeight;
      const finalHeight = Math.max(formPageHeight, minFormHeight);
      
      if (window.parent && window.parent !== window) {
        window.parent.postMessage(
          { type: 'resize', height: finalHeight },
          '*'
        );
      }
      
      isSendingRef.current = false;
      return;
    }
    
    const isPaymentForm = window.location.href.includes('liqpay') || 
                         document.querySelector('iframe[src*="liqpay"]') ||
                         document.querySelector('.liqpay-checkout') ||
                         document.querySelector('#liqpay_checkout') ||
                         document.querySelector('form[action*="liqpay"]');
    
    if (isPaymentForm) {
      const paymentForm = document.querySelector('iframe[src*="liqpay"]') ||
                         document.querySelector('.liqpay-checkout') ||
                         document.querySelector('#liqpay_checkout') ||
                         document.querySelector('form[action*="liqpay"]') ||
                         document.body;
      
      const paymentFormHeight = paymentForm ? 
        (paymentForm.offsetHeight || paymentForm.scrollHeight || 600) : 600;
      
      const minPaymentHeight = 600;
      const finalHeight = Math.max(paymentFormHeight, minPaymentHeight);
      
      if (window.parent && window.parent !== window) {
        window.parent.postMessage(
          { type: 'resize', height: finalHeight },
          '*'
        );
      }
      
      isSendingRef.current = false;
      return;
    }
    
    isSendingRef.current = true;
    
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      requestAnimationFrame(() => {
        const mainContent = getMainContent();
        const hasExpanded = hasExpandedAccordions();
        const calculatedFromSections = calculateFromSections();
        
        let actualHeight = 0;
        
        if (hasExpanded) {
          actualHeight = calculateExpandedHeight(calculatedFromSections, mainContent);
        } else {
          actualHeight = calculateCollapsedHeight(calculatedFromSections);
        }
        
        const messengerSection = document.querySelector('.skipass-messenger-instructions-section');
        const hasMessengerInstructions = messengerSection && messengerSection.offsetHeight > 0;
        
        const minHeight = hasMessengerInstructions ? 1120 : 510;
        
        if (actualHeight === 0 || actualHeight < 100) {
          actualHeight = window.innerHeight || minHeight;
        }
        
        if (actualHeight < minHeight) {
          actualHeight = minHeight;
        }
        
        const paddingToAdd = hasExpanded 
          ? extraPaddingForLastSection
          : extraPaddingForLastSection;
        
        const heightWithPadding = Math.min(actualHeight + paddingToAdd, MAX_HEIGHT);
        const heightDiff = Math.abs(heightWithPadding - lastHeightRef.current);
        const previousHeight = lastHeightRef.current;
        const now = Date.now();
        const timeSinceLastUpdate = now - lastUpdateTimeRef.current;
        const isCurrentlyCollapsed = !hasExpanded;
        
        if (isCurrentlyCollapsed) {
          const collapsedHeight = calculateCollapsedHeight(calculatedFromSections);
          
          const messengerContainer = document.querySelector('.skipass-messenger-instructions-container');
          let messengerHeight = 0;
          if (messengerContainer) {
            const messengerSection = document.querySelector('.skipass-messenger-instructions-section');
            if (messengerSection && messengerSection.offsetHeight > 0) {
              messengerHeight = messengerContainer.offsetHeight || messengerSection.offsetHeight || 0;
            }
          }
          
          const finalCollapsedHeight = Math.max(collapsedHeight + messengerHeight, minHeight);
          const collapsedHeightWithPadding = Math.min(
            finalCollapsedHeight + extraPaddingForLastSection,
            MAX_HEIGHT
          );
          
          lastHeightRef.current = collapsedHeightWithPadding;
          lastUpdateTimeRef.current = now;
          
          if (window.parent && window.parent !== window) {
            window.parent.postMessage(
              { type: 'resize', height: collapsedHeightWithPadding },
              '*'
            );
          }
          
          isSendingRef.current = false;
          return;
        }
        
        const pricingLayout = document.querySelector('.skipass-pricing-layout');
        let currentHeightFromLayout = 0;
        
        if (pricingLayout) {
          currentHeightFromLayout = pricingLayout.offsetHeight || 0;
        }
        
        const messengerContainer = document.querySelector('.skipass-messenger-instructions-container');
        let messengerHeight = 0;
        if (messengerContainer) {
          const messengerSection = document.querySelector('.skipass-messenger-instructions-section');
          if (messengerSection && messengerSection.offsetHeight > 0) {
            messengerHeight = messengerContainer.offsetHeight || messengerSection.offsetHeight || 0;
          }
        }
        
        const baseHeight = currentHeightFromLayout > 0 ? currentHeightFromLayout : calculatedFromSections;
        const finalBaseHeight = Math.max(baseHeight + messengerHeight, minHeight);
        
        const currentHeightWithPadding = Math.min(
          finalBaseHeight + 
          (hasExpanded ? extraPaddingForLastSection + 50 : extraPaddingForLastSection),
          MAX_HEIGHT
        );
        
        if (currentHeightWithPadding < previousHeight || (pricingLayout && currentHeightFromLayout > 0)) {
          lastHeightRef.current = currentHeightWithPadding;
          lastUpdateTimeRef.current = now;
          
          if (window.parent && window.parent !== window) {
            window.parent.postMessage(
              { type: 'resize', height: currentHeightWithPadding },
              '*'
            );
          }
          
          isSendingRef.current = false;
          return;
        }
        
        const shouldForceUpdate = forceUpdateRef.current;
        if (shouldForceUpdate) {
          forceUpdateRef.current = false;
        }
        
        if (heightDiff > 3 || shouldForceUpdate) {
          if (shouldForceUpdate || timeSinceLastUpdate > (updateCooldownMs / 3)) {
            if (heightWithPadding > previousHeight && previousHeight > 0 && !shouldForceUpdate) {
              const increaseAmount = heightWithPadding - previousHeight;
              const increasePercent = ((heightWithPadding - previousHeight) / previousHeight) * 100;
              
              if (increaseAmount >= 45 && increaseAmount <= 55 && timeSinceLastUpdate < 1500) {
                isSendingRef.current = false;
                return;
              }
              
              if (increasePercent > 50 && timeSinceLastUpdate < 1000) {
                isSendingRef.current = false;
                return;
              }
            }
            
            lastHeightRef.current = heightWithPadding;
            lastUpdateTimeRef.current = now;
            
            if (window.parent && window.parent !== window) {
              window.parent.postMessage(
                { type: 'resize', height: heightWithPadding },
                '*'
              );
            }
          }
        }
        
        isSendingRef.current = false;
      });
    }, debounceMs);
  };


  useEffect(() => {
    const handleResize = () => sendHeight();
    window.addEventListener('resize', handleResize);
    
    let resizeObserverTimeout = null;
    const resizeObserver = new ResizeObserver(() => {
      clearTimeout(resizeObserverTimeout);
      resizeObserverTimeout = setTimeout(() => {
        requestAnimationFrame(() => sendHeight());
      }, 200);
    });

    const containersToObserve = [
      containerRef?.current,
      document.querySelector('.skipass-pricing-layout'),
      document.querySelector('.skipass-main-content'),
      document.querySelector('.skipass-App'),
      document.querySelector('main')
    ].filter(Boolean);

    containersToObserve.forEach(container => {
      resizeObserver.observe(container);
    });

    const observeAccordions = () => {
      const accordionSections = document.querySelectorAll('.skipass-accordion-section');
      accordionSections.forEach(section => {
        resizeObserver.observe(section);
        const content = section.querySelector('.skipass-accordion-content');
        if (content) {
          resizeObserver.observe(content);
        }
      });
    };

    observeAccordions();

    const cartSection = document.querySelector('.skipass-cart-section');
    if (cartSection) {
      resizeObserver.observe(cartSection);
      const cartTableBody = cartSection.querySelector('.skipass-table-body') ||
                           cartSection.querySelector('.skipass-cart-product-table .skipass-table-body') ||
                           cartSection.querySelector('.skipass-cart-items');
      if (cartTableBody) {
        resizeObserver.observe(cartTableBody);
      }
    }

    const messengerSection = document.querySelector('.skipass-messenger-instructions-section');
    if (messengerSection) {
      resizeObserver.observe(messengerSection);
      const messengerContainer = messengerSection.querySelector('.skipass-messenger-instructions-container');
      if (messengerContainer) {
        resizeObserver.observe(messengerContainer);
      }
    }

    const formPage = document.querySelector('.skipass-form-page');
    if (formPage) {
      resizeObserver.observe(formPage);
      const formContentWrapper = formPage.querySelector('.skipass-form-content-wrapper');
      if (formContentWrapper) {
        resizeObserver.observe(formContentWrapper);
      }
    }

    const handleAccordionToggle = (isExpanding) => {
      if (isExpanding) {
        forceUpdateRef.current = true;
        
        const estimatedAccordionHeight = 800;
        const predictiveHeight = lastHeightRef.current + estimatedAccordionHeight + extraPaddingForLastSection + 300;
        
        if (window.parent && window.parent !== window) {
          window.parent.postMessage(
            { type: 'resize', height: Math.min(predictiveHeight, MAX_HEIGHT) },
            '*'
          );
        }
        
        setTimeout(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => sendHeight());
          });
        }, 200);
        
        const updateTimes = [300, 400, 500, 600, 700, 800, 1000, 1200, 1500, 2000];
        updateTimes.forEach((delay, index) => {
          setTimeout(() => {
            if (index < 8) {
              forceUpdateRef.current = true;
            }
            requestAnimationFrame(() => {
              requestAnimationFrame(() => sendHeight());
            });
          }, delay);
        });
      } else {
        requestAnimationFrame(() => sendHeight());
        
        const collapseUpdateTimes = [50, 100, 150, 200, 300, 400, 500];
        collapseUpdateTimes.forEach(delay => {
          setTimeout(() => {
            requestAnimationFrame(() => sendHeight());
          }, delay);
        });
      }
    };

    const mutationObserver = new MutationObserver((mutations) => {
      const newAccordionSections = document.querySelectorAll('.skipass-accordion-section');
      newAccordionSections.forEach(section => {
        if (!section.dataset.observed) {
          section.dataset.observed = 'true';
          resizeObserver.observe(section);
          const content = section.querySelector('.skipass-accordion-content');
          if (content) {
            resizeObserver.observe(content);
          }
        }
      });

      const hasAccordionToggle = mutations.some(mutation => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target;
          if (target.classList && (
            target.classList.contains('skipass-accordion-header') ||
            target.classList.contains('skipass-expanded') ||
            target.closest('.skipass-accordion-section')
          )) {
            return true;
          }
        }
        if (mutation.type === 'childList') {
          const target = mutation.target;
          if (target.closest && (
            target.closest('.skipass-accordion-section') ||
            target.classList?.contains('skipass-accordion-content') ||
            target.querySelector('.skipass-accordion-content')
          )) {
            return true;
          }
        }
        return false;
      });

      const hasCartChanges = mutations.some(mutation => {
        const target = mutation.target;
        return target.closest && (
          target.closest('.skipass-cart-section') ||
          target.closest('.skipass-cart-product-table') ||
          target.closest('.skipass-table-body') ||
          target.closest('.skipass-cart-items')
        );
      });

      const hasMessengerChanges = mutations.some(mutation => {
        const target = mutation.target;
        return target.closest && (
          target.closest('.skipass-messenger-instructions-section') ||
          target.closest('.skipass-messenger-instructions-container') ||
          target.classList?.contains('skipass-messenger-instructions-section') ||
          target.classList?.contains('skipass-messenger-instructions-container')
        );
      });

      const hasFormPageChanges = mutations.some(mutation => {
        const target = mutation.target;
        return target.closest && (
          target.closest('.skipass-form-page') ||
          target.closest('.skipass-form-content-wrapper') ||
          target.closest('.skipass-selected-product-form') ||
          target.classList?.contains('skipass-form-page') ||
          target.classList?.contains('skipass-form-content-wrapper') ||
          target.classList?.contains('skipass-selected-product-form')
        );
      });

      if (hasAccordionToggle) {
        const expandedAccordions = document.querySelectorAll('.skipass-accordion-header.skipass-expanded');
        const isExpanding = expandedAccordions.length > 0;
        handleAccordionToggle(isExpanding);
      }

      if (hasCartChanges) {
        setTimeout(() => sendHeight(), 50);
      }

      if (hasMessengerChanges) {
        setTimeout(() => sendHeight(), 50);
        requestAnimationFrame(() => sendHeight());
      }

      if (hasFormPageChanges) {
        setTimeout(() => sendHeight(), 50);
        requestAnimationFrame(() => sendHeight());
      }

      if (hasAccordionToggle || hasCartChanges || hasMessengerChanges || hasFormPageChanges) {
        requestAnimationFrame(() => sendHeight());
      }
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });

    setTimeout(() => sendHeight(), 100);
    setTimeout(() => sendHeight(), 400);

    return () => {
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      clearTimeout(timeoutRef.current);
      if (resizeObserverTimeout) clearTimeout(resizeObserverTimeout);
    };
  }, [containerRef, debounceMs, basePadding, extraPaddingForLastSection]);

  return { sendHeight };
};
